/**
 * Cliente para la balanza Systel/Torrey via print-bridge HTTP.
 *
 * El bridge corre en la PC del kiosko (localhost:9101) y hace polling
 * de la balanza por COM virtual (USB-Serial). Acá solo consumimos sus
 * endpoints HTTP — no requiere Tauri.
 */

const BRIDGE_URL =
  (import.meta as { env: { VITE_BRIDGE_URL?: string } }).env.VITE_BRIDGE_URL ||
  "http://localhost:9101";

export interface ScaleConfig {
  model: string;
  port: string;
  baud: number;
}

export const DEFAULT_SCALE: ScaleConfig = {
  model: "systel",
  port: "COM1",
  baud: 9600,
};

export interface ScaleStatus {
  weight: number;
  stable: boolean;
  connected: boolean;
  receiving_data: boolean;
  seconds_since_data: number;
  disconnect_reason: string | null;
  port: string;
  baudrate: number;
  model: string;
  simulation: boolean;
}

export interface ScalePort {
  device: string;
  description: string;
  manufacturer: string;
  vid: string | null;
  pid: string | null;
  likely_scale: boolean;
}

async function bridgeFetch(
  path: string,
  init?: RequestInit,
  timeoutMs = 3000
): Promise<Response> {
  const ctrl = new AbortController();
  const id = setTimeout(() => ctrl.abort(), timeoutMs);
  try {
    return await fetch(`${BRIDGE_URL}${path}`, {
      ...(init ?? {}),
      signal: ctrl.signal,
    });
  } finally {
    clearTimeout(id);
  }
}

export async function readWeight(): Promise<number> {
  const r = await bridgeFetch("/scale/weight");
  if (!r.ok) {
    throw new Error(
      `Bridge respondió ${r.status} — balanza no configurada en el bridge`
    );
  }
  const data = (await r.json()) as ScaleStatus;
  if (!data.connected) {
    throw new Error(
      data.disconnect_reason ||
        `Balanza no conectada en ${data.port}. Revisá cable USB.`
    );
  }
  return data.weight;
}

export async function scaleStatus(): Promise<ScaleStatus | null> {
  try {
    const r = await bridgeFetch("/scale/status");
    if (!r.ok) return null;
    return (await r.json()) as ScaleStatus;
  } catch {
    return null;
  }
}

export async function listScalePorts(): Promise<ScalePort[]> {
  try {
    const r = await bridgeFetch("/scale/ports");
    if (!r.ok) return [];
    const data = (await r.json()) as { ports: ScalePort[] };
    return data.ports || [];
  } catch {
    return [];
  }
}

export async function setScaleConfig(cfg: {
  port: string;
  baudrate: number;
  model: string;
}): Promise<ScaleStatus> {
  const r = await bridgeFetch("/scale/config", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(cfg),
  });
  if (!r.ok) {
    const txt = await r.text();
    throw new Error(`Bridge ${r.status}: ${txt}`);
  }
  return (await r.json()) as ScaleStatus;
}
